<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');
const activeFilter = ref('all');
const statusByInboxId = ref({});
const bridgeError = ref(false);
let pollTimer = null;

const inboxes = useMapGetter('inboxes/getInboxes');
const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);

const enrichedList = computed(() =>
  (inboxes.value || []).map(inbox => {
    const enrich = statusByInboxId.value[inbox.id] || {};
    return {
      ...inbox,
      _status: enrich.status || 'no_whatsapp',
      _phone: enrich.phone_number || null,
      _lastMessageAt: enrich.last_message_at || null,
      _createdAt: inbox.created_at || enrich.created_at || null,
    };
  })
);

const counts = computed(() => {
  const c = { connected: 0, disconnected: 0, all: enrichedList.value.length };
  enrichedList.value.forEach(i => {
    if (i._status === 'connected') c.connected += 1;
    else if (i._status === 'disconnected') c.disconnected += 1;
  });
  return c;
});

const filteredList = computed(() => {
  let list = enrichedList.value;
  const q = searchQuery.value.trim().toLowerCase();
  if (q) list = list.filter(i => (i.name || '').toLowerCase().includes(q));
  if (activeFilter.value === 'connected') {
    list = list.filter(i => i._status === 'connected');
  } else if (activeFilter.value === 'disconnected') {
    list = list.filter(i => i._status === 'disconnected');
  }
  return list.slice().sort((a, b) => a.name.localeCompare(b.name));
});

const MONTH_PT = [
  'Jan.', 'Fev.', 'Mar.', 'Abr.', 'Mai.', 'Jun.',
  'Jul.', 'Ago.', 'Set.', 'Out.', 'Nov.', 'Dez.',
];
const formatDate = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getDate()} ${MONTH_PT[d.getMonth()]} ${d.getFullYear()}`;
};

const fetchStatus = async () => {
  if (!currentAccountId.value) return;
  try {
    const resp = await fetch(
      `/evogo2-bridge/inboxes-status?account_id=${currentAccountId.value}`,
      { credentials: 'include' }
    );
    if (!resp.ok) throw new Error(`bridge status ${resp.status}`);
    const body = await resp.json();
    const map = {};
    (body.inboxes || []).forEach(it => {
      map[it.inbox_id] = it;
    });
    statusByInboxId.value = map;
    bridgeError.value = false;
  } catch {
    bridgeError.value = true;
  }
};

onMounted(() => {
  fetchStatus();
  pollTimer = setInterval(fetchStatus, 30_000);
});

onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer);
});

const copyPhone = async phone => {
  if (!phone) return;
  try {
    await navigator.clipboard.writeText(phone);
    useAlert(t('INBOX_MGMT.CHANNELS_REDESIGN.COPIED'));
  } catch {
    useAlert(t('INBOX_MGMT.CHANNELS_REDESIGN.COPY_FAILED'));
  }
};

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);
const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);
const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);
const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};
const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!enrichedList.length && !uiFlags.isFetching"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <h1
        class="text-xl font-medium tracking-tight text-n-slate-12 channels-page-title"
      >
        {{ $t('INBOX_MGMT.CHANNELS_REDESIGN.HEADER') }}
      </h1>
    </template>
    <template #body>
      <div class="channels-toolbar">
        <div class="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            class="channels-filter-chip"
            :class="{ active: activeFilter === 'connected' }"
            @click="activeFilter = 'connected'"
          >
            {{ $t('INBOX_MGMT.CHANNELS_REDESIGN.FILTER.CONNECTED') }}
            <span class="channels-filter-count">{{ counts.connected }}</span>
          </button>
          <button
            type="button"
            class="channels-filter-chip"
            :class="{ active: activeFilter === 'disconnected' }"
            @click="activeFilter = 'disconnected'"
          >
            {{ $t('INBOX_MGMT.CHANNELS_REDESIGN.FILTER.DISCONNECTED') }}
            <span class="channels-filter-count">{{ counts.disconnected }}</span>
          </button>
          <button
            type="button"
            class="channels-filter-chip"
            :class="{ active: activeFilter === 'all' }"
            @click="activeFilter = 'all'"
          >
            {{ $t('INBOX_MGMT.CHANNELS_REDESIGN.FILTER.ALL') }}
            <span class="channels-filter-count">{{ counts.all }}</span>
          </button>
        </div>
        <div class="channels-toolbar-right">
          <div class="channels-search-wrap">
            <span class="i-lucide-search channels-search-icon" />
            <input
              v-model="searchQuery"
              type="search"
              class="channels-search-input"
              :placeholder="
                $t('INBOX_MGMT.CHANNELS_REDESIGN.SEARCH_PLACEHOLDER')
              "
            />
          </div>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button
              icon="i-lucide-circle-plus"
              :label="$t('INBOX_MGMT.CHANNELS_REDESIGN.NEW_CHANNEL_BUTTON')"
            />
          </router-link>
        </div>
      </div>

      <div
        v-if="bridgeError"
        class="text-xs text-n-slate-10 mb-3 px-3 py-2 rounded bg-n-alpha-2"
      >
        {{ $t('INBOX_MGMT.CHANNELS_REDESIGN.BRIDGE_UNAVAILABLE') }}
      </div>

      <div
        v-if="!filteredList.length"
        class="channels-empty-filter"
      >
        {{ $t('INBOX_MGMT.CHANNELS_REDESIGN.NO_FILTER_MATCH') }}
      </div>
      <div v-else class="flex flex-col gap-3">
        <div
          v-for="inbox in filteredList"
          :key="inbox.id"
          class="channels-card"
        >
          <div class="channels-card-main">
            <svg
              v-if="inbox._status === 'connected' || inbox._status === 'disconnected'"
              class="channels-avatar-svg"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z"
                fill="#4A4D52"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M16 8C11.5817 8 8 11.5817 8 16C8 17.5769 8.4552 19.0444 9.24098 20.2818C9.46769 20.6389 9.54552 21.0892 9.41832 21.5217L8.97667 23.0233L10.4783 22.5817C10.9108 22.4545 11.3611 22.5323 11.7182 22.759C12.9556 23.5448 14.4231 24 16 24C20.4183 24 24 20.4183 24 16C24 11.5817 20.4183 8 16 8ZM6 16C6 10.4772 10.4772 6 16 6C21.5228 6 26 10.4772 26 16C26 21.5228 21.5228 26 16 26C14.1104 26 12.3406 25.475 10.8317 24.5624L7.78217 25.4594C7.43109 25.5626 7.05165 25.4659 6.79289 25.2071C6.53413 24.9483 6.43738 24.5689 6.54063 24.2178L7.43756 21.1683C6.52505 19.6594 6 17.8896 6 16Z"
                fill="#9797A0"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M18.374 20.5764C17.692 20.5513 15.7608 20.2847 13.7383 18.2622C11.7158 16.2397 11.4491 14.3084 11.424 13.6265C11.4169 13.4313 11.4492 13.2521 11.4871 13.1004L11.4946 13.0706C11.706 12.225 12.3005 11.5269 13.1016 11.1836C13.3175 11.0911 13.5789 11.108 13.7856 11.2588C14.4678 11.7564 14.9599 12.4837 15.379 13.1032C15.4248 13.1708 15.4697 13.2372 15.5138 13.3018C15.7166 13.5984 15.6681 14.0033 15.3927 14.2417L14.4693 14.9275C14.3778 14.9955 14.3473 15.119 14.4022 15.219C14.6112 15.5993 14.9834 16.1652 15.4093 16.5912C15.8357 17.0175 16.4284 17.4138 16.8348 17.6462C16.9402 17.7065 17.0728 17.6716 17.1394 17.5701L17.7402 16.655C17.9705 16.3485 18.4007 16.2815 18.7134 16.4979C18.7554 16.527 18.7979 16.5563 18.8407 16.5858C19.4768 17.0246 20.1925 17.5183 20.7255 18.2009C20.8872 18.408 20.9112 18.6787 20.8148 18.9037C20.4728 19.7017 19.7773 20.294 18.935 20.5046L18.9 20.5133C18.7483 20.5513 18.5691 20.5836 18.374 20.5764Z"
                fill="#9797A0"
              />
            </svg>
            <div v-else class="channels-avatar">
              <ChannelIcon
                class="size-6 text-[#9797A0]"
                :inbox="inbox"
              />
            </div>
            <div class="channels-name-block">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="channels-name">{{ inbox.name }}</span>
                <span
                  v-if="inbox._status === 'connected'"
                  class="channels-badge channels-badge-connected"
                >
                  {{
                    $t('INBOX_MGMT.CHANNELS_REDESIGN.STATUS.CONNECTED')
                  }}
                </span>
                <span
                  v-else-if="inbox._status === 'disconnected'"
                  class="channels-badge channels-badge-disconnected"
                >
                  {{
                    $t('INBOX_MGMT.CHANNELS_REDESIGN.STATUS.DISCONNECTED')
                  }}
                </span>
              </div>
              <span
                v-if="inbox._status === 'connected' || inbox._status === 'disconnected'"
                class="channels-channel-label"
                >WhatsApp</span
              >
              <ChannelName
                v-else
                :channel-type="inbox.channel_type"
                :medium="inbox.medium"
                class="channels-channel-label"
              />
            </div>
          </div>

          <div class="channels-col">
            <div class="channels-col-label">
              {{ $t('INBOX_MGMT.CHANNELS_REDESIGN.COLUMN.PHONE') }}
            </div>
            <div class="channels-col-value flex items-center gap-1">
              <template v-if="inbox._phone">
                <span>{{ inbox._phone }}</span>
                <button
                  v-tooltip.top="
                    $t('INBOX_MGMT.CHANNELS_REDESIGN.COPY_PHONE')
                  "
                  type="button"
                  class="channels-copy-btn"
                  @click="copyPhone(inbox._phone)"
                >
                  <svg
                    class="channels-copy-icon"
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M3.33333 1.66667C3.33333 0.746192 4.07953 0 5 0H13.3333C14.2538 0 15 0.746192 15 1.66667V10C15 10.9205 14.2538 11.6667 13.3333 11.6667H11.6667V13.3333C11.6667 14.2538 10.9205 15 10 15H1.66667C0.746192 15 0 14.2538 0 13.3333V5C0 4.07952 0.746192 3.33333 1.66667 3.33333H3.33333V1.66667ZM5 3.33333H10C10.9205 3.33333 11.6667 4.07953 11.6667 5V10H13.3333V1.66667H5V3.33333ZM10 5H1.66667V13.3333H10V5Z"
                      fill="#388E3C"
                    />
                  </svg>
                </button>
              </template>
              <span v-else class="text-n-slate-10">{{
                $t('INBOX_MGMT.CHANNELS_REDESIGN.PHONE_NONE')
              }}</span>
            </div>
          </div>

          <div class="channels-col">
            <div class="channels-col-label">
              {{ $t('INBOX_MGMT.CHANNELS_REDESIGN.COLUMN.CREATED_AT') }}
            </div>
            <div class="channels-col-value">
              {{ formatDate(inbox._createdAt) }}
            </div>
          </div>

          <div class="channels-col">
            <div class="channels-col-label">
              {{
                $t('INBOX_MGMT.CHANNELS_REDESIGN.COLUMN.LAST_MESSAGE_AT')
              }}
            </div>
            <div class="channels-col-value">
              {{ formatDate(inbox._lastMessageAt) }}
            </div>
          </div>

          <div class="channels-actions">
            <router-link
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: inbox.id },
              }"
            >
              <button
                v-if="isAdmin"
                v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                type="button"
                class="channels-action-btn"
              >
                <span class="i-lucide-settings size-5" />
              </button>
            </router-link>
            <button
              v-if="isAdmin"
              v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
              type="button"
              class="channels-action-btn"
              @click="openDelete(inbox)"
            >
              <span class="i-lucide-trash-2 size-5" />
            </button>
          </div>
        </div>
      </div>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>

<style scoped>
.channels-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 4px 0 8px;
  box-sizing: border-box;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  color: rgb(var(--slate-11));
  background: transparent;
  border: 1px solid rgb(var(--border-weak));
  border-radius: 999px;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  cursor: pointer;
}
.channels-filter-chip:hover {
  color: rgb(var(--slate-12));
}
.channels-filter-chip.active {
  background: rgba(var(--alpha-3));
  border-color: rgb(var(--border-strong));
  color: rgb(var(--slate-12));
}
.channels-filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  font-size: 11px;
  font-weight: 500;
  color: rgb(var(--slate-11));
  background: rgba(var(--alpha-2));
  border-radius: 999px;
}
.channels-filter-chip.active .channels-filter-count {
  color: rgb(var(--slate-12));
  background: rgba(var(--alpha-3));
}

.channels-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.channels-toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.channels-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.channels-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: rgb(var(--slate-11));
  pointer-events: none;
}
.channels-search-input {
  height: 40px;
  width: 341px;
  max-width: 100%;
  padding: 0 12px 0 36px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 13px;
  color: rgb(var(--slate-12));
  background: transparent;
  border: 1px solid rgb(var(--border-weak));
  border-radius: 8px;
  outline: none;
}
.channels-search-input::placeholder {
  color: rgb(var(--slate-11));
}
.channels-search-input:focus {
  border-color: rgb(var(--border-strong));
}

@media (max-width: 640px) {
  .channels-search-input {
    width: 100%;
  }
  .channels-toolbar-right {
    width: 100%;
  }
}

.channels-card {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid rgb(var(--border-weak));
  border-radius: 8px;
  width: 100%;
  min-height: 68px;
  box-sizing: border-box;
}

.channels-card-main {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex: 1 1 240px;
  min-width: 200px;
}
.channels-avatar {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border-radius: 999px;
  background: #4a4d52;
  display: flex;
  align-items: center;
  justify-content: center;
}
.channels-name-block {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
}
.channels-name {
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: rgb(var(--slate-12));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.channels-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 500;
  line-height: 1;
  border-radius: 29px;
  white-space: nowrap;
}
.channels-badge-connected {
  background: #194121;
  color: #98c79d;
}
.channels-badge-disconnected {
  background: #4d1c1f;
  color: #e89a9d;
}
.channels-empty-filter {
  padding: 32px 12px;
  text-align: center;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 14px;
  color: rgb(var(--slate-11));
}
.channels-channel-label {
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: rgb(var(--slate-11));
}

.channels-col {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 0 0 200px;
  min-width: 0;
}
.channels-col-label {
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: rgb(var(--slate-12));
}
.channels-col-value {
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: rgb(var(--slate-11));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.channels-copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 2.5px;
  background: transparent;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
}
.channels-copy-icon {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.channels-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}
.channels-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 8px;
  background: transparent;
  border: 1.5px solid rgb(var(--border-weak));
  border-radius: 8px;
  color: rgb(var(--slate-12));
  cursor: pointer;
  transition: background 0.15s;
}
.channels-action-btn:hover {
  background: rgba(var(--alpha-2));
}

@media (max-width: 1280px) {
  .channels-col:nth-child(4) {
    display: none;
  }
}
@media (max-width: 1024px) {
  .channels-col:nth-child(3) {
    display: none;
  }
}
@media (max-width: 768px) {
  .channels-col {
    display: none;
  }
  .channels-card {
    flex-wrap: wrap;
  }
}
</style>
